import React, { useState } from "react";
import { X, UserCog } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PrimaryButton } from "@/components/kit";
import { inputClass } from "@/components/AuthShell";

export default function EditProfileModal({ open, onClose, onEmailChanged }) {
  const { user, updateProfile } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phoneContact, setPhoneContact] = useState(user?.phone_contact || "");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedName) return toast.error("Please enter your name.");
    if (!trimmedEmail) return toast.error("Please enter your email.");

    const payload = {};
    if (trimmedName !== user?.name) payload.name = trimmedName;
    if (trimmedEmail !== (user?.email || "").toLowerCase()) payload.email = trimmedEmail;
    if (phoneContact.trim() !== (user?.phone_contact || "")) payload.phone_contact = phoneContact.trim();
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    setLoading(true);
    try {
      const res = await updateProfile(payload);
      if (res.email_verification_sent) {
        toast.success("Profile updated. We sent a verification code to your new email.");
        onClose();
        onEmailChanged && onEmailChanged();
      } else {
        toast.success("Profile updated.");
        onClose();
      }
    } catch (err) {
      toast.error(err.message || "Could not update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-profile-modal">
        <div className="flex justify-end">
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#F2EFE9] flex items-center justify-center" data-testid="edit-profile-close">
            <X size={18} />
          </button>
        </div>
        <div className="text-center -mt-2">
          <div className="h-16 w-16 rounded-3xl bg-[#F6DED5] flex items-center justify-center mx-auto mb-3">
            <UserCog size={30} color="#E86A45" />
          </div>
          <h2 className="font-display text-2xl font-extrabold">Edit profile</h2>
          <p className="text-[#4A4746] mt-1 text-sm">Update your name, login email, or contact details.</p>
        </div>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <div>
            <label className="block text-sm font-bold mb-1.5">Name</label>
            <input
              data-testid="edit-profile-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-1.5">Email (login)</label>
            <input
              data-testid="edit-profile-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
            <p className="text-xs text-[#9A938B] mt-1.5">
              This is the email you log in with — changing it updates your login. We&apos;ll email a 6-digit code to verify the new address. You&apos;ll stay signed in.
            </p>
          </div>
          <div>
            <label className="block text-sm font-bold mb-1.5">Contact phone (optional)</label>
            <input
              data-testid="edit-profile-phone"
              type="tel"
              inputMode="tel"
              value={phoneContact}
              onChange={(e) => setPhoneContact(e.target.value)}
              placeholder="+1 555 123 4567"
              className={inputClass}
            />
            <p className="text-xs text-[#9A938B] mt-1.5">
              Just your contact number — <b>not</b> used for text alerts. To receive SMS, verify a number under Notifications.
            </p>
          </div>
          <PrimaryButton data-testid="edit-profile-save" type="submit" loading={loading} className="w-full mt-1">
            Save changes
          </PrimaryButton>
        </form>
      </div>
    </div>
  );
}
